/* ==========================================================================
   mm-flyby.js — M Machina header flyby

   ── WHY THIS WAS REWRITTEN ────────────────────────────────────────────────
   The previous version ended the flyby by removing the .is-run class on a
   setTimeout. That is what caused the animation to cut out at varying
   intervals, and it was wrong twice over:

     1. Removing the class kills every animation instantly, discarding the
        `forwards` fill. Mid-flight, that is a hard cut — the jet vanishes and
        the trail snaps back to zero width.

     2. The timing could not be right. The animation starts at FIRST PAINT,
        because .is-run is server-rendered. This script is deferred, so it
        executes only after the whole document is parsed and after
        constants/pubsub/swiper-bundle/jarallax/global/scroll-image-reveal have
        all been fetched and run. The gap between those two moments varies with
        network and CPU on every single load — which is exactly where "random,
        varying intervals" came from.

   So nothing here removes .is-run any more. Every animation now ends itself
   with a finite iteration count and a `forwards` fill, in CSS, on the
   compositor's own clock. This script cannot cut the animation short because
   it no longer has a mechanism to.

   What it still does:
     · releases the compositor hints once the flight has genuinely finished
     · re-fires on back/forward navigation, which does not re-run scripts

   Both are now optimisations rather than requirements. If this file fails to
   load, the flyby still plays correctly from start to finish.
   ========================================================================== */
(function () {
  'use strict';

  var el = document.querySelector('.mm-flyby');
  if (!el) return;

  /* Anchored: absolutely positioned against .mm-flyby-anchor, so its vertical
     placement is exact and nothing needs measuring. */
  var anchored = el.classList.contains('mm-flyby--anchored') ||
                 el.classList.contains('mm-flyby--in-header');

  /* ------------------------------------------------------------------------
     OPTIONAL · once per session.

     Shopify does a full page load on every click — there is no router in this
     theme's script list — so every navigation fires the flyby. That is what
     was asked for. If it starts to grate, uncomment this.

     Wrapped because sessionStorage throws in Safari private mode and in
     sandboxed frames. It must fail OPEN: a storage error replays the
     animation, it never breaks the header.
     ------------------------------------------------------------------------ */
  // try {
  //   if (sessionStorage.getItem('mm-flyby') === '1') { el.remove(); return; }
  //   sessionStorage.setItem('mm-flyby', '1');
  // } catch (e) { /* fails open */ }

  /* ------------------------------------------------------------------------
     FIXED-MODE ANCHORING · skipped entirely when anchored.

     Shopify puts .shopify-section-group-header-group on EVERY section in the
     group — announcement bar, header, anything else. Taking the first match
     would measure the announcement bar and drop the contrail through the
     middle of the header. Take the group's lowest edge.
     ------------------------------------------------------------------------ */
  function headerBottom() {
    var explicit = document.querySelector('[data-mm-header]');
    if (explicit) return explicit.getBoundingClientRect().bottom;

    var parts = document.querySelectorAll('.shopify-section-group-header-group');
    if (parts.length) {
      var low = 0;
      for (var i = 0; i < parts.length; i++) {
        var b = parts[i].getBoundingClientRect().bottom;
        if (b > low) low = b;
      }
      return low;
    }
    var h = document.querySelector('header');
    return h ? h.getBoundingClientRect().bottom : 0;
  }

  function anchor() {
    if (anchored) return;
    /* One rect read, no writes before it, so no layout thrash. Sanity-checked:
       a header measuring 0 or 900 means we found the wrong element, and a wrong
       number is worse than the CSS default. A page restored mid-scroll can also
       yield a negative bottom. */
    var b = Math.round(headerBottom());
    if (b > 8 && b < 400) el.style.setProperty('--mm-top', b + 'px');
  }

  /* ------------------------------------------------------------------------
     RELEASE

     will-change is a standing request to the compositor to keep a layer alive;
     left set it costs GPU memory for the life of the page. Clearing the INLINE
     value (to empty, not to 'auto') lets the CSS declaration apply again on a
     restart, rather than being permanently overridden.

     This is now purely an optimisation. Calling it early would waste a little
     GPU memory. Calling it never would waste a little GPU memory. Neither can
     interrupt the animation, which is the whole point of the rewrite.
     ------------------------------------------------------------------------ */
  var layers = el.querySelectorAll('.mm-trail, .mm-jet');

  function release() {
    for (var i = 0; i < layers.length; i++) layers[i].style.willChange = 'auto';
  }
  function reclaim() {
    for (var i = 0; i < layers.length; i++) layers[i].style.willChange = '';
  }

  /* The cool-off on the hairline is the last thing in the sequence to finish,
     so its animationend is the honest signal that the flight is over — no
     duration arithmetic, no assumption about when this script started running.
     The timeout is a backstop for the case where the tab is backgrounded and
     animationend never arrives. */
  var cool = el.querySelector('.mm-trail__cool');
  if (cool) cool.addEventListener('animationend', release, { once: true });
  setTimeout(release, 5000);

  /* ------------------------------------------------------------------------
     EVERY PAGE LOAD

     A normal navigation in this theme is a full document load, so the
     server-rendered .is-run class handles it and there is nothing to do.

     Back and forward navigation is the exception: the browser restores from
     bfcache, which does NOT re-run scripts or re-parse HTML, so the animation
     would already be finished in the restored snapshot. Without this it would
     silently stop firing on back-button navigation.

     This is the ONE place the class is touched, and it is a deliberate re-run
     rather than a teardown — removed and re-added within the same frame, so
     there is no window in which a partial state is visible.
     ------------------------------------------------------------------------ */
  function replay() {
    el.classList.remove('is-run');
    /* Forced reflow — the only way to restart a CSS animation from script.
       Cheap: once per navigation, not per frame. */
    void el.offsetWidth;
    reclaim();
    anchor();
    el.classList.add('is-run');
    if (cool) cool.addEventListener('animationend', release, { once: true });
    setTimeout(release, 5000);
  }

  window.addEventListener('pageshow', function (e) {
    if (e.persisted) replay();
  });

  anchor();

  /* Fixed mode only: if the header changes height after load — a lazy
     announcement bar, a web font settling, a breakpoint — re-anchor. Guarded so
     it does nothing mid-flight, to avoid a visible jump. Anchored mode needs
     none of this, because the wrapper moves with the header automatically. */
  if (!anchored && window.ResizeObserver) {
    var parts = document.querySelectorAll('[data-mm-header], .shopify-section-group-header-group');
    if (parts.length) {
      var ro = new ResizeObserver(function () {
        if (!el.classList.contains('is-run')) anchor();
      });
      for (var i = 0; i < parts.length; i++) ro.observe(parts[i]);
    }
  }
})();
